// The Workflow V2 API is defined here: https://developer.atlassian.com/cloud/jira-workflow/rest/api-group-workflow-v-/

import { useCallback } from 'react';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { fetchJson, FetchError } from '@atlassian/jira-fetch';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	NO_INBOUND_TRANSITIONS_TO_STATUS,
	NON_UNIQUE_STATUS_NAME,
	VERSION_CONFLICT,
} from './constants';
import {
	StatusNameIsNotUniqueError,
	StatusHasNoIncomingTransitionError,
	WorkflowVersionConflictError,
} from './errors';
import type {
	FetchWorkflowsV2Param,
	WorkflowV2Response,
	WorkflowV2UpdatePayload,
	WorkflowUpdateV2Response,
	WorkflowValidationErrorList,
	WorkflowV2UpdateValidationRequest,
} from './types';

export { mapWorkflowToWorkflowUpdateObject, mapStatusesToStatusUpdateObjects } from './utils';
export type {
	StatusUpdate,
	Transition,
	WorkflowStatus,
	WorkflowV2Response,
	FetchWorkflowsV2Param,
	WorkflowValidationErrorList,
	WorkflowUpdateV2Response,
} from './types';
export {
	StatusNameIsNotUniqueError,
	StatusHasNoIncomingTransitionError,
	WorkflowVersionConflictError,
} from './errors';

// Replace with lodash/noop
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export const useWorkflowsV2 = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fetchWorkflowsV2 = useCallback(
		async ({
			cloudId,
			projectAndIssueTypes,
		}: FetchWorkflowsV2Param): Promise<WorkflowV2Response> => {
			try {
				const response = await fetchJson<WorkflowV2Response>(
					`/gateway/api/jira/project-configuration/query/${cloudId}/2/workflow`,
					{
						method: 'POST',
						body: JSON.stringify({
							projectAndIssueTypes,
						}),
					},
				);
				return response;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				const jsonErrorResponse = error.originalResponse
					? await error.originalResponse.json().catch(noop)
					: undefined;

				if (jsonErrorResponse) {
					const errorMessage = `Fetch workflow fail status code: ${
						error.statusCode
					}; response: ${JSON.stringify(jsonErrorResponse)}`;

					const errorWithResponseMessage = new FetchError(
						error.statusCode,
						errorMessage,
						error.traceId,
					);

					fireErrorAnalytics({
						meta: {
							id: 'fetchBoardWorkflowsV2',
							packageName: 'jiraWorkManagementBoard',
							teamName: 'wanjel',
						},
						error: errorWithResponseMessage,
						sendToPrivacyUnsafeSplunk: true,
					});

					throw errorWithResponseMessage;
				}

				fireErrorAnalytics({
					meta: {
						id: 'fetchBoardWorkflowsV2',
						packageName: 'jiraWorkManagementBoard',
						teamName: 'wanjel',
					},
					attributes: {
						message: 'Failed to fetch board workflows',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});

				throw error;
			}
		},
		[],
	);
	/**
	 * Update a single workflow only
	 * The `workflow` array in the payload can only have maximum 1 length
	 */
	const updateWorkflowsV2 = useCallback(
		async ({ payload, cloudId }: { payload: WorkflowV2UpdatePayload; cloudId: string }) => {
			try {
				const response = await fetchJson<WorkflowUpdateV2Response>(
					`/gateway/api/jira/project-configuration/command/${cloudId}/2/workflow/update`,
					{
						method: 'POST',
						body: JSON.stringify(payload),
					},
				);
				return response;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				const jsonErrorResponse = error.originalResponse
					? await error.originalResponse.json().catch(noop)
					: undefined;

				if (jsonErrorResponse) {
					const errorMessage = `Update workflow fail status code: ${
						error.statusCode
					}; response: ${JSON.stringify(jsonErrorResponse)}`;

					const errorWithResponseMessage = new FetchError(
						error.statusCode,
						errorMessage,
						error.traceId,
					);

					fireErrorAnalytics({
						meta: {
							id: 'updateBoardWorkflowsV2',
							packageName: 'jiraWorkManagementBoard',
							teamName: 'wanjel',
						},
						error: errorWithResponseMessage,
						sendToPrivacyUnsafeSplunk: true,
					});

					throw errorWithResponseMessage;
				}

				fireErrorAnalytics({
					meta: {
						id: 'updateBoardWorkflowsV2',
						packageName: 'jiraWorkManagementBoard',
						teamName: 'wanjel',
					},
					attributes: {
						message: 'Failed to update board workflows',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});

				throw error;
			}
		},
		[],
	);

	/**
	 * Validates the update payload and calls update workflow if there are no validation errors
	 * The `workflow` array in the payload can only have maximum 1 length
	 */
	const validateAndUpdateWorkflowsV2 = useCallback(
		async ({
			payload,
			cloudId,
		}: {
			payload: WorkflowV2UpdateValidationRequest;
			cloudId: string;
		}): Promise<WorkflowUpdateV2Response> => {
			// validate payload
			let validationResponse: WorkflowValidationErrorList;
			try {
				validationResponse = await fetchJson<WorkflowValidationErrorList>(
					`/gateway/api/jira/project-configuration/command/${cloudId}/2/workflow/update/validation`,
					{
						method: 'POST',
						body: JSON.stringify(payload),
					},
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				const jsonErrorResponse = error.originalResponse
					? await error.originalResponse.json().catch(noop)
					: undefined;

				if (jsonErrorResponse) {
					const errorMessage = `Validate workflow fail status code: ${
						error.statusCode
					}; response: ${JSON.stringify(jsonErrorResponse)}`;

					const errorWithResponseMessage = new FetchError(
						error.statusCode,
						errorMessage,
						error.traceId,
					);

					fireErrorAnalytics({
						meta: {
							id: 'validateUpdateBoardWorkflowsV2',
							packageName: 'jiraWorkManagementBoard',
							teamName: 'wanjel',
						},
						error: errorWithResponseMessage,
						sendToPrivacyUnsafeSplunk: true,
					});

					throw errorWithResponseMessage;
				}

				fireErrorAnalytics({
					meta: {
						id: 'validateUpdateBoardWorkflowsV2',
						packageName: 'jiraWorkManagementBoard',
						teamName: 'wanjel',
					},
					attributes: {
						message: 'Failed to validate payload for update workflows',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});

				throw error;
			}

			if (validationResponse.errors.length > 0) {
				validationResponse.errors.forEach((e) => {
					if (e.code === NON_UNIQUE_STATUS_NAME) {
						fireTrackAnalytics(createAnalyticsEvent({}), 'workflowValidation failed', {
							message: e.message,
							code: e.code,
						});
						throw new StatusNameIsNotUniqueError();
					}

					if (e.code === NO_INBOUND_TRANSITIONS_TO_STATUS) {
						fireTrackAnalytics(createAnalyticsEvent({}), 'workflowValidation failed', {
							message: e.message,
							code: e.code,
						});
						throw new StatusHasNoIncomingTransitionError();
					}

					if (e.code === VERSION_CONFLICT) {
						throw new WorkflowVersionConflictError();
					}
				});

				const errorMsgs = validationResponse.errors.map((e) => e.message).join(', ');
				const errorCodes = validationResponse.errors.map((e) => e.code).join(', ');
				const errorMessage = `Validation error(s) in update workflows payload - ${errorMsgs}`;
				const error = Error(errorMessage);

				fireErrorAnalytics({
					meta: {
						id: 'validateUpdateBoardWorkflowsV2',
						packageName: 'jiraWorkManagementBoard',
						teamName: 'wanjel',
					},
					attributes: {
						message: errorMessage,
						totalErrors: validationResponse.errors.length,
						code: errorCodes,
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});

				throw error;
			}

			// update workflow
			return updateWorkflowsV2({
				payload: payload.payload,
				cloudId,
			});
		},
		[updateWorkflowsV2, createAnalyticsEvent],
	);

	return {
		fetchWorkflowsV2,
		updateWorkflowsV2,
		validateAndUpdateWorkflowsV2,
	};
};
